import React, { ComponentType, FC, forwardRef } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { T } from 'ia-react-core/~/interfaces/t';
import getWrappedDisplayName from '../../../utils/hocs/getWrappedDisplayName';
import Translation from '../../../services/translation/Translation';
import useTranslation from '../../../hooks/useTranslation';
import { APP_NAMESPACE } from './injectTranslations.constants';

export interface InjectableTranslations<T> {
  language: string;
  resource: T;
}

export interface InjectTranslationsProps {
  ref?: React.ForwardedRef<unknown>;
}

export interface InjectTranslationsType<Translations> {
  (namespace: string, translations: InjectableTranslations<Translations>[]): (WrappedComponent: ComponentType<Translations>) => ComponentType<Translations & InjectTranslationsProps>;
  (translations: InjectableTranslations<Translations>[]): (WrappedComponent: ComponentType<Translations>) => ComponentType<Translations & InjectTranslationsProps>;
}

export interface InjectTranslationsMock {}

/**
 * @since 0.13.0
 * @visibleName injectTranslations
 * @component
 */
const injectTranslations = <Translations, >(
  ...args: [
    namespace: string,
    translations: InjectableTranslations<Translations>[] | Record<string, Translations>
  ] |
  [
    translations: InjectableTranslations<Translations>[] | Record<string, Translations>
  ]
) => {
  const [arg1, arg2] = args;
  let namespace: string;
  let translationConfigs: InjectableTranslations<Translations>[] | Record<string, Translations>;

  if (typeof arg1 !== 'string') {
    namespace = APP_NAMESPACE;
    translationConfigs = arg1;
  } else {
    namespace = arg1;
    translationConfigs = arg2;
  }

  const resourcesList = Array.isArray(translationConfigs) ?
    translationConfigs :
    Object.keys(translationConfigs).map((language) => ({
      language,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      resource: (translationConfigs as Record<string, Translations>)[language],
    }));

  resourcesList.forEach((translationConfig) => {
    const { language, resource } = translationConfig;
    Translation.addTranslation(language, namespace, resource);
  });

  return (WrappedComponent: FC<InjectTranslationsProps & { t: T }>) => {
    const InjectTranslation = forwardRef<unknown, InjectTranslationsProps>((props, ref) => {
      const { t } = useTranslation(namespace);
      return (
        <WrappedComponent {...props} t={t} ref={ref} />
      );
    });

    InjectTranslation.displayName = getWrappedDisplayName('WithTranslation', WrappedComponent);

    return hoistNonReactStatics(InjectTranslation, WrappedComponent);
  };
};

export default injectTranslations;
